import type { OnboardingTemplate, OnboardingTemplateStep } from 'src/interfaces/onboardingTemplate'
import { mapApiRowToEditorStep } from 'src/utils/collectDataTemplateEditor/stepApiMapping'
import { filterDeprecatedCollectDataSteps } from 'src/utils/collectData'
import type { FlowEditorStepGroupFieldOverrides } from 'src/utils/collectDataTemplateEditor/types'
import { normalizeCollectDataTemplateConfigId } from 'src/utils/collectDataTemplateEditor/configIds'
import type {
  FlowEditorConfigDraft,
  FlowEditorStepDraft,
  FlowEditorTemplateDocument,
} from './types'
import { isPersistedServerId } from './ids'

function configDraftFromApi(
  stepsConfig: OnboardingTemplate['collectDataTemplateStepsConfigs'][number],
  stepLocalIds: string[],
): FlowEditorConfigDraft {
  const apiId = normalizeCollectDataTemplateConfigId(stepsConfig.id) || stepsConfig.id
  const persisted = isPersistedServerId(apiId)

  return {
    localId: apiId,
    serverId: persisted ? apiId : undefined,
    label: stepsConfig.label,
    delayed: stepsConfig.delayed,
    title: stepsConfig.title,
    description: stepsConfig.description,
    order: stepsConfig.order,
    insurancePolicyId: stepsConfig.insurancePolicyId,
    stepLocalIds,
  }
}

function stepDraftFromEditorStep(
  editorStep: OnboardingTemplateStep,
  configLocalId: string,
): FlowEditorStepDraft {
  const apiId = editorStep.id
  const persisted = isPersistedServerId(apiId)

  return {
    localId: apiId,
    serverId: persisted ? apiId : undefined,
    configLocalId,
    type: editorStep.type,
    order: editorStep.order,
    collectDataTemplateStepLabel: editorStep.collectDataTemplateStepLabel,
    retroactiveCollectRequired: editorStep.retroactiveCollectRequired,
    ignoreMonitoring: editorStep.ignoreMonitoring,
    recollectSameTypeSteps: editorStep.recollectSameTypeSteps,
    skipRetroactiveCollectIfAnySameTypeCompleted:
      editorStep.skipRetroactiveCollectIfAnySameTypeCompleted,
    config: editorStep.config,
  }
}

/** Build a document from the API template (baseline after fetch / save). */
export function templateToDocument(template: OnboardingTemplate): FlowEditorTemplateDocument {
  const configs = [...(template.collectDataTemplateStepsConfigs ?? [])].sort(
    (a, b) => a.order - b.order,
  )
  const configsByLocalId: Record<string, FlowEditorConfigDraft> = {}
  const stepsByLocalId: Record<string, FlowEditorStepDraft> = {}
  const configLocalIds: string[] = []

  for (const stepsConfig of configs) {
    const configLocalId = normalizeCollectDataTemplateConfigId(stepsConfig.id) || stepsConfig.id
    const rows = filterDeprecatedCollectDataSteps(
      [...(stepsConfig.collectDataTemplateSteps ?? [])].sort((a, b) => a.order - b.order),
    )
    const stepLocalIds: string[] = []

    for (const row of rows) {
      const editorStep = mapApiRowToEditorStep(row, stepsConfig)
      const stepDraft = stepDraftFromEditorStep(editorStep, configLocalId)

      stepLocalIds.push(stepDraft.localId)
      stepsByLocalId[stepDraft.localId] = stepDraft
    }

    configsByLocalId[configLocalId] = configDraftFromApi(stepsConfig, stepLocalIds)
    configLocalIds.push(configLocalId)
  }

  return {
    templateId: template.id,
    name: template.name,
    partnerId: template.partnerId,
    configLocalIds,
    configsByLocalId,
    stepsByLocalId,
  }
}

export function documentToGroupSettings(
  document: FlowEditorTemplateDocument,
): Record<string, FlowEditorStepGroupFieldOverrides> {
  const out: Record<string, FlowEditorStepGroupFieldOverrides> = {}

  for (const configLocalId of document.configLocalIds) {
    const config = document.configsByLocalId[configLocalId]

    if (!config) {
      continue
    }

    out[configLocalId] = {
      delayed: config.delayed,
      label: config.label?.trim() !== '' ? config.label : 'Step group',
      insurancePolicyId: config.insurancePolicyId ?? null,
    }
  }

  return out
}
